/**
 * AssociatedObjectsSection - Sección de objetos asociados para EdgeDetailsPanel
 *
 * @remarks
 * Muestra los objetos puntuales (árboles, paradas, luminarias, etc.)
 * asociados a un segmento de red. Permite ver agrupados por tipo
 * y expandir para ver detalles.
 *
 * @see tracking/GRAMATICA_ASOCIACIONES_PUNTO_RED.md
 */

import { useState } from 'react';
import {
  Link,
  ChevronDown,
  ChevronUp,
  TreePine,
  Lightbulb,
  Bus,
  Trash2,
  MapPin,
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
  HelpCircle,
  RefreshCw,
  Lock,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { useEdgeAssociations, type AssociationSummary } from '../hooks/useEdgeAssociations';
import type { AssociationStatus } from '../services/point-association-api.service';
import './associated-objects-section.css';

interface AssociatedObjectsSectionProps {
  projectId: string;
  edgeId: string;
  onSelectEntity?: (entityType: string, entityId: string) => void;
}

/**
 * Iconos por tipo de entidad
 */
const ENTITY_ICONS: Record<string, LucideIcon> = {
  tree: TreePine,
  transit_stop: Bus,
  streetlight: Lightbulb,
  waste_container: Trash2,
};

/**
 * Obtiene el icono para un tipo de entidad
 */
function EntityIcon({ type, size = 14 }: { type: string; size?: number }): JSX.Element {
  const Icon = ENTITY_ICONS[type] || MapPin;
  return <Icon size={size} />;
}

/**
 * Icono de estado de asociación
 */
function StatusIcon({ status }: { status: AssociationStatus }): JSX.Element {
  switch (status) {
    case 'valid':
      return <CheckCircle2 size={10} className="status-icon valid" />;
    case 'weak':
      return <Zap size={10} className="status-icon weak" />;
    case 'needs_review':
      return <AlertCircle size={10} className="status-icon needs-review" />;
    case 'needs_recalculation':
    case 'recalculated':
      return <RefreshCw size={10} className="status-icon pending" />;
    case 'manual_lock':
      return <Lock size={10} className="status-icon locked" />;
    case 'orphaned':
      return <AlertTriangle size={10} className="status-icon orphaned" />;
    default:
      return <HelpCircle size={10} className="status-icon unknown" />;
  }
}

/**
 * Componente principal
 */
export function AssociatedObjectsSection({
  projectId,
  edgeId,
  onSelectEntity,
}: AssociatedObjectsSectionProps): JSX.Element | null {
  const { data, isLoading, error } = useEdgeAssociations(projectId, edgeId);
  const [expandedType, setExpandedType] = useState<string | null>(null);

  // No mostrar si está cargando o hay error
  if (isLoading) {
    return (
      <section className="associated-objects-section loading">
        <div className="section-header">
          <Link size={14} />
          <span>Objetos Asociados</span>
        </div>
        <div className="loading-indicator">
          <RefreshCw size={14} className="spin" />
          <span>Cargando...</span>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="associated-objects-section error">
        <div className="section-header">
          <Link size={14} />
          <span>Objetos Asociados</span>
        </div>
        <div className="error-message">
          <AlertCircle size={14} />
          <span>Error al cargar</span>
        </div>
      </section>
    );
  }

  // No mostrar si no hay datos o no hay asociaciones
  if (!data || data.total === 0) {
    return (
      <section className="associated-objects-section empty">
        <div className="section-header">
          <Link size={14} />
          <span>Objetos Asociados</span>
        </div>
        <div className="empty-message">
          <span>Sin objetos asociados</span>
        </div>
      </section>
    );
  }

  const toggleExpand = (type: string) => {
    setExpandedType(expandedType === type ? null : type);
  };

  const handleEntityClick = (assoc: AssociationSummary) => {
    if (onSelectEntity) {
      onSelectEntity(assoc.entityType, assoc.entityId);
    }
  };

  return (
    <section className="associated-objects-section">
      <div className="section-header">
        <Link size={14} />
        <span>Objetos Asociados</span>
        <span className="total-count">({data.total})</span>
      </div>

      <div className="entity-types-list">
        {Object.entries(data.byEntityType).map(([type, info]) => (
          <div key={type} className="entity-type-group">
            <button
              className="entity-type-row"
              onClick={() => toggleExpand(type)}
            >
              <EntityIcon type={type} />
              <span className="type-label">{info.label}</span>
              <span className="type-count">{info.count}</span>
              {expandedType === type ? (
                <ChevronUp size={14} />
              ) : (
                <ChevronDown size={14} />
              )}
            </button>

            {expandedType === type && (
              <div className="associations-list">
                {data.associations
                  .filter((a) => a.entityType === type)
                  .map((assoc) => (
                    <button
                      key={assoc.id}
                      className={`association-row ${assoc.isPrimary ? 'primary' : ''}`}
                      onClick={() => handleEntityClick(assoc)}
                      title={`ID: ${assoc.entityId}\nDistancia: ${assoc.distance?.toFixed(1) ?? '?'}m\nEstado: ${assoc.status}`}
                    >
                      <StatusIcon status={assoc.status} />
                      <span className="entity-id">
                        {assoc.entityId.slice(0, 8)}...
                      </span>
                      <span className="distance">
                        {assoc.distance?.toFixed(1) ?? '?'}m
                      </span>
                      {assoc.side && (
                        <span className="side">
                          {assoc.side === 'left' ? 'Izq' : assoc.side === 'right' ? 'Der' : ''}
                        </span>
                      )}
                      {assoc.isPrimary && (
                        <span className="primary-badge">P</span>
                      )}
                    </button>
                  ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

export default AssociatedObjectsSection;
